import { AxiosInstance } from 'axios';
import qs from 'qs';

import { OffsetPaginationInput } from '@app/data/inputs';
import { CollectionResource } from '@app/data/resources';

import {
    CancelSubscriptionInput,
    CreateSubscriptionInput,
    ProcessLatestSubscriptionInvoiceInput,
    SubscriptionFilterInput,
    SyncSubscriptionInput,
    UpdatePaymentMethodInput,
    UpdateSubscriptionItemInput,
    UpdateSubscriptionItemsBatchInput,
    UpdateSubscriptionPauseInput,
} from './inputs';
import { FlexMonthRef, SubscriptionRef } from './refs';
import { FlexMonthStatusResource, SubscriptionResource } from './resources';

export default class Subscription {
    private basePath = '/api/subscription';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Force-sync subscription from external source
    sync = async (input: SyncSubscriptionInput): Promise<SubscriptionRef> => {
        const { data } = await this.http.post(`${this.basePath}/`, input);

        return data;
    };

    // Get subscription list
    get = async (
        pagination: OffsetPaginationInput,
        filter: SubscriptionFilterInput
    ): Promise<CollectionResource<SubscriptionResource, {}>> => {
        const query = qs.stringify({ ...pagination, ...filter });

        const { data } = await this.http.get(`${this.basePath}/?${query}`);

        return data;
    };

    // Get single subscription
    getById = async (id: ID): Promise<SubscriptionResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}`);

        return data;
    };

    // Create new uncompleted subscription
    create = async (input: CreateSubscriptionInput): Promise<SubscriptionRef> => {
        const { data } = await this.http.post(`${this.basePath}/`, input);

        return data;
    };

    // Process latest subscription invoice
    processLatestInvoice = async (id: ID, input: ProcessLatestSubscriptionInvoiceInput): Promise<SubscriptionRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/latest-invoice/process`, input);

        return data;
    };

    // Switch price inside subscription
    updateSubscriptionItemsBatch = async (
        id: ID,
        input: UpdateSubscriptionItemsBatchInput
    ): Promise<SubscriptionRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/items`, input);

        return data;
    };

    // Switch price inside subscription
    updateSubscriptionItemPrice = async (
        id: ID,
        priceId: ID,
        input: UpdateSubscriptionItemInput
    ): Promise<SubscriptionRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/price/${priceId}`, input);

        return data;
    };

    // Release all scheduled subscription updates
    cancelScheduledUpdates = async (id: ID): Promise<SubscriptionRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/schedule/release`);

        return data;
    };

    // Disable subscription auto-renew (schedule cancellation)
    cancelSubscription = async (id: ID, input: CancelSubscriptionInput): Promise<SubscriptionRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/auto-renew/disable`, input);

        return data;
    };

    // Enable subscription auto-renew (do not cancel)
    doNotCancelSubscription = async (id: ID): Promise<SubscriptionRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/auto-renew/enable`);

        return data;
    };

    // Set subscription pause schedule (and pause subscription if active)
    updatePause = async (id: ID, input: UpdateSubscriptionPauseInput): Promise<SubscriptionRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/pause`, input);
        return data;
    };

    // Release subscription pause schedule (resume subscription)
    removePause = async (id: ID): Promise<SubscriptionRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/pause/release`);
        return data;
    };

    // Get Flex Month status (eligibility, current flex month, button-ready UI state)
    getFlexMonth = async (id: ID): Promise<FlexMonthStatusResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}/flex-month`);
        return data;
    };

    // Schedule a Flex Month (one-month billing pause; dates computed server-side)
    scheduleFlexMonth = async (id: ID): Promise<FlexMonthRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/flex-month`);
        return data;
    };

    // Cancel the scheduled Flex Month (allowed strictly before the T−48h cutoff)
    cancelFlexMonth = async (id: ID): Promise<FlexMonthRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/flex-month/cancel`);
        return data;
    };

    // Set / Update payment method
    updatePaymentMethod = async (id: ID, input: UpdatePaymentMethodInput): Promise<SubscriptionRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/payment-method`, input);

        return data;
    };
}
